import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { PortalHeader } from "@/components/PortalHeader";
import { EntityDetailsSection } from "@/components/EntityDetailsSection";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Loader2 } from "lucide-react";
import { verifyEndUserDetails } from "@/services/onBoardingFormServices";
import { ReusableDropdown } from "@/components/ui/re-usable-dropdown";
import { useToast } from "@/hooks/use-toast";
import { ASP_ACCREDIATION_NUMBER } from "@/config/env";

export const countryOptions = [
  { value: "AD", label: "Andorra" },
  { value: "AE", label: "United Arab Emirates" },
  { value: "AF", label: "Afghanistan" },
  { value: "AG", label: "Antigua and Barbuda" },
  { value: "AI", label: "Anguilla" },
  { value: "AL", label: "Albania" },
  { value: "AM", label: "Armenia" },
  { value: "AO", label: "Angola" },
  { value: "AQ", label: "Antarctica" },
  { value: "AR", label: "Argentina" },
  { value: "AS", label: "American Samoa" },
  { value: "AT", label: "Austria" },
  { value: "AU", label: "Australia" },
  { value: "AW", label: "Aruba" },
  { value: "AX", label: "Åland Islands" },
  { value: "AZ", label: "Azerbaijan" },
  { value: "BA", label: "Bosnia and Herzegovina" },
  { value: "BB", label: "Barbados" },
  { value: "BD", label: "Bangladesh" },
  { value: "BE", label: "Belgium" },
  { value: "BF", label: "Burkina Faso" },
  { value: "BG", label: "Bulgaria" },
  { value: "BH", label: "Bahrain" },
  { value: "BI", label: "Burundi" },
  { value: "BJ", label: "Benin" },
  { value: "BL", label: "Saint Barthélemy" },
  { value: "BM", label: "Bermuda" },
  { value: "BN", label: "Brunei Darussalam" },
  { value: "BO", label: "Bolivia, Plurinational State of" },
  { value: "BQ", label: "Bonaire, Sint Eustatius and Saba" },
  { value: "BR", label: "Brazil" },
  { value: "BS", label: "Bahamas" },
  { value: "BT", label: "Bhutan" },
  { value: "BV", label: "Bouvet Island" },
  { value: "BW", label: "Botswana" },
  { value: "BY", label: "Belarus" },
  { value: "BZ", label: "Belize" },
  { value: "CA", label: "Canada" },
  { value: "CC", label: "Cocos (Keeling) Islands" },
  { value: "CD", label: "Congo, the Democratic Republic of the" },
  { value: "CF", label: "Central African Republic" },
  { value: "CG", label: "Congo" },
  { value: "CH", label: "Switzerland" },
  { value: "CI", label: "Côte d'Ivoire" },
  { value: "CK", label: "Cook Islands" },
  { value: "CL", label: "Chile" },
  { value: "CM", label: "Cameroon" },
  { value: "CN", label: "China" },
  { value: "CO", label: "Colombia" },
  { value: "CR", label: "Costa Rica" },
  { value: "CU", label: "Cuba" },
  { value: "CV", label: "Cabo Verde" },
  { value: "CW", label: "Curaçao" },
  { value: "CX", label: "Christmas Island" },
  { value: "CY", label: "Cyprus" },
  { value: "CZ", label: "Czechia" },
  { value: "DE", label: "Germany" },
  { value: "DJ", label: "Djibouti" },
  { value: "DK", label: "Denmark" },
  { value: "DM", label: "Dominica" },
  { value: "DO", label: "Dominican Republic" },
  { value: "DZ", label: "Algeria" },
  { value: "EC", label: "Ecuador" },
  { value: "EE", label: "Estonia" },
  { value: "EG", label: "Egypt" },
  { value: "EH", label: "Western Sahara" },
  { value: "ER", label: "Eritrea" },
  { value: "ES", label: "Spain" },
  { value: "ET", label: "Ethiopia" },
  { value: "FI", label: "Finland" },
  { value: "FJ", label: "Fiji" },
  { value: "FK", label: "Falkland Islands (Malvinas)" },
  { value: "FM", label: "Micronesia, Federated States of" },
  { value: "FO", label: "Faroe Islands" },
  { value: "FR", label: "France" },
  { value: "GA", label: "Gabon" },
  { value: "GB", label: "United Kingdom of Great Britain and Northern Ireland" },
  { value: "GD", label: "Grenada" },
  { value: "GE", label: "Georgia" },
  { value: "GF", label: "French Guiana" },
  { value: "GG", label: "Guernsey" },
  { value: "GH", label: "Ghana" },
  { value: "GI", label: "Gibraltar" },
  { value: "GL", label: "Greenland" },
  { value: "GM", label: "Gambia" },
  { value: "GN", label: "Guinea" },
  { value: "GP", label: "Guadeloupe" },
  { value: "GQ", label: "Equatorial Guinea" },
  { value: "GR", label: "Greece" },
  { value: "GS", label: "South Georgia and the South Sandwich Islands" },
  { value: "GT", label: "Guatemala" },
  { value: "GU", label: "Guam" },
  { value: "GW", label: "Guinea-Bissau" },
  { value: "GY", label: "Guyana" },
  { value: "HK", label: "Hong Kong" },
  { value: "HM", label: "Heard Island and McDonald Islands" },
  { value: "HN", label: "Honduras" },
  { value: "HR", label: "Croatia" },
  { value: "HT", label: "Haiti" },
  { value: "HU", label: "Hungary" },
  { value: "ID", label: "Indonesia" },
  { value: "IE", label: "Ireland" },
  { value: "IL", label: "Israel" },
  { value: "IM", label: "Isle of Man" },
  { value: "IN", label: "India" },
  { value: "IO", label: "British Indian Ocean Territory" },
  { value: "IQ", label: "Iraq" },
  { value: "IR", label: "Iran, Islamic Republic of" },
  { value: "IS", label: "Iceland" },
  { value: "IT", label: "Italy" },
  { value: "JE", label: "Jersey" },
  { value: "JM", label: "Jamaica" },
  { value: "JO", label: "Jordan" },
  { value: "JP", label: "Japan" },
  { value: "KE", label: "Kenya" },
  { value: "KG", label: "Kyrgyzstan" },
  { value: "KH", label: "Cambodia" },
  { value: "KI", label: "Kiribati" },
  { value: "KM", label: "Comoros" },
  { value: "KN", label: "Saint Kitts and Nevis" },
  { value: "KP", label: "Korea, Democratic People's Republic of" },
  { value: "KR", label: "Korea, Republic of" },
  { value: "KW", label: "Kuwait" },
  { value: "KY", label: "Cayman Islands" },
  { value: "KZ", label: "Kazakhstan" },
  { value: "LA", label: "Lao People's Democratic Republic" },
  { value: "LB", label: "Lebanon" },
  { value: "LC", label: "Saint Lucia" },
  { value: "LI", label: "Liechtenstein" },
  { value: "LK", label: "Sri Lanka" },
  { value: "LR", label: "Liberia" },
  { value: "LS", label: "Lesotho" },
  { value: "LT", label: "Lithuania" },
  { value: "LU", label: "Luxembourg" },
  { value: "LV", label: "Latvia" },
  { value: "LY", label: "Libya" },
  { value: "MA", label: "Morocco" },
  { value: "MC", label: "Monaco" },
  { value: "MD", label: "Moldova, Republic of" },
  { value: "ME", label: "Montenegro" },
  { value: "MF", label: "Saint Martin (French part)" },
  { value: "MG", label: "Madagascar" },
  { value: "MH", label: "Marshall Islands" },
  { value: "MK", label: "Macedonia, the former Yugoslav Republic of" },
  { value: "ML", label: "Mali" },
  { value: "MM", label: "Myanmar" },
  { value: "MN", label: "Mongolia" },
  { value: "MO", label: "Macao" },
  { value: "MP", label: "Northern Mariana Islands" },
  { value: "MQ", label: "Martinique" },
  { value: "MR", label: "Mauritania" },
  { value: "MS", label: "Montserrat" },
  { value: "MT", label: "Malta" },
  { value: "MU", label: "Mauritius" },
  { value: "MV", label: "Maldives" },
  { value: "MW", label: "Malawi" },
  { value: "MX", label: "Mexico" },
  { value: "MY", label: "Malaysia" },
  { value: "MZ", label: "Mozambique" },
  { value: "NA", label: "Namibia" },
  { value: "NC", label: "New Caledonia" },
  { value: "NE", label: "Niger" },
  { value: "NF", label: "Norfolk Island" },
  { value: "NG", label: "Nigeria" },
  { value: "NI", label: "Nicaragua" },
  { value: "NL", label: "Netherlands" },
  { value: "NO", label: "Norway" },
  { value: "NP", label: "Nepal" },
  { value: "NR", label: "Nauru" },
  { value: "NU", label: "Niue" },
  { value: "NZ", label: "New Zealand" },
  { value: "OM", label: "Oman" },
  { value: "PA", label: "Panama" },
  { value: "PE", label: "Peru" },
  { value: "PF", label: "French Polynesia" },
  { value: "PG", label: "Papua New Guinea" },
  { value: "PH", label: "Philippines" },
  { value: "PK", label: "Pakistan" },
  { value: "PL", label: "Poland" },
  { value: "PM", label: "Saint Pierre and Miquelon" },
  { value: "PN", label: "Pitcairn" },
  { value: "PR", label: "Puerto Rico" },
  { value: "PS", label: "Palestine, State of" },
  { value: "PT", label: "Portugal" },
  { value: "PW", label: "Palau" },
  { value: "PY", label: "Paraguay" },
  { value: "QA", label: "Qatar" },
  { value: "RE", label: "Réunion" },
  { value: "RO", label: "Romania" },
  { value: "RS", label: "Serbia" },
  { value: "RU", label: "Russian Federation" },
  { value: "RW", label: "Rwanda" },
  { value: "SA", label: "Saudi Arabia" },
  { value: "SB", label: "Solomon Islands" },
  { value: "SC", label: "Seychelles" },
  { value: "SD", label: "Sudan" },
  { value: "SE", label: "Sweden" },
  { value: "SG", label: "Singapore" },
  { value: "SH", label: "Saint Helena, Ascension and Tristan da Cunha" },
  { value: "SI", label: "Slovenia" },
  { value: "SJ", label: "Svalbard and Jan Mayen" },
  { value: "SK", label: "Slovakia" },
  { value: "SL", label: "Sierra Leone" },
  { value: "SM", label: "San Marino" },
  { value: "SN", label: "Senegal" },
  { value: "SO", label: "Somalia" },
  { value: "SR", label: "Suriname" },
  { value: "SS", label: "South Sudan" },
  { value: "ST", label: "Sao Tome and Principe" },
  { value: "SV", label: "El Salvador" },
  { value: "SX", label: "Sint Maarten (Dutch part)" },
  { value: "SY", label: "Syrian Arab Republic" },
  { value: "SZ", label: "Swaziland" },
  { value: "TC", label: "Turks and Caicos Islands" },
  { value: "TD", label: "Chad" },
  { value: "TF", label: "French Southern Territories" },
  { value: "TG", label: "Togo" },
  { value: "TH", label: "Thailand" },
  { value: "TJ", label: "Tajikistan" },
  { value: "TK", label: "Tokelau" },
  { value: "TL", label: "Timor-Leste" },
  { value: "TM", label: "Turkmenistan" },
  { value: "TN", label: "Tunisia" },
  { value: "TO", label: "Tonga" },
  { value: "TR", label: "Turkey" },
  { value: "TT", label: "Trinidad and Tobago" },
  { value: "TV", label: "Tuvalu" },
  { value: "TW", label: "Taiwan, Province of China" },
  { value: "TZ", label: "Tanzania, United Republic of" },
  { value: "UA", label: "Ukraine" },
  { value: "UG", label: "Uganda" },
  { value: "UM", label: "United States Minor Outlying Islands" },
  { value: "US", label: "United States of America" },
  { value: "UY", label: "Uruguay" },
  { value: "UZ", label: "Uzbekistan" },
  { value: "VA", label: "Holy See" },
  { value: "VC", label: "Saint Vincent and the Grenadines" },
  { value: "VE", label: "Venezuela, Bolivarian Republic of" },
  { value: "VG", label: "Virgin Islands, British" },
  { value: "VI", label: "Virgin Islands, U.S." },
  { value: "VN", label: "Viet Nam" },
  { value: "VU", label: "Vanuatu" },
  { value: "WF", label: "Wallis and Futuna" },
  { value: "WS", label: "Samoa" },
  { value: "YE", label: "Yemen" },
  { value: "YT", label: "Mayotte" },
  { value: "ZA", label: "South Africa" },
  { value: "ZM", label: "Zambia" },
  { value: "ZW", label: "Zimbabwe" },
  { value: "1A", label: "Kosovo" },
  { value: "XI", label: "United Kingdom (Northern Ireland)" }
];
const formSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters"),

  TIN: z
    .string()
    .superRefine((val, ctx) => {
      if (!/^\d*$/.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Only numbers are allowed",
        });
        return;
      }
      if (!val.startsWith("1")) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "TIN must start with 1",
        });
        return;
      }
      if (val.length !== 10) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "TIN must be 10 digits only",
        });
        return;
      }
    }),

  countryCode: z
    .string()
    .min(1, "Country code is required"),

  mobile: z
    .string()
    .optional()
    .refine(
      (val) => !val || /^[0-9]+$/.test(val),
      "Only digits are allowed"
    )
    .refine(
      (val) => !val || val.length === 10,
      "Mobile number must be exactly 10 digits"
    ),
});

const LinkingRequest = () => {
  const navigate = useNavigate();
  const { toast, dismiss } = useToast();
  const { search, pathname } = useLocation();
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [declaration, setDeclaration] = useState(false);
  const [emarataxToken, setEmaraTaxToken] = useState('');
  const [verificationStatus, setVerificationStatus] = useState(false);
  const [isVerifyDisabled, setIsVerifyDisabled] = useState(false);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      email: "",
      TIN: "",
      countryCode: "AE",
      mobile: "",
    },
  });
  const { handleSubmit, trigger, watch, setValue, reset, formState: { errors } } = form;

  const [entityDetails, setEntityDetails] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let code = params.get("authcode");
    if (code) {
      setEmaraTaxToken(code);
      params.delete("authcode");
      navigate(`${pathname}${params.toString() ? `?${params.toString()}` : ""}`, {
        replace: true
      });
    }
  }, [search, pathname, navigate]);

  const tin = watch("TIN");
  const email = watch("email");
  useEffect(() => {
    const isTinValid = /^\d{10}$/.test(tin) && !errors?.TIN;
    const isEmailValid = email && !errors?.email;
    setIsVerifyDisabled(!(isTinValid && isEmailValid));
  }, [tin, email, errors?.email, errors?.TIN]);


  const handleVerify = async (data: any) => {
    const isValid = await form.trigger();
    if (!isValid) return;
    if (!emarataxToken) {
      toast({
        title: "",
        description: "EmaraTax Token Can't Be Null !!",
        variant: "error",
        duration: 10000
      });
      return;
    }
    setIsVerifying(true);
    let payload = {
      "TIN": data.TIN,
      "email": data.email,
      "mobile": data.mobile,
      "emarataxToken": emarataxToken,
      "aspId": ASP_ACCREDIATION_NUMBER
    }
    await verifyEndUserDetails(payload).then(res => {
      if (res.success) {
        if (res.data) {
          setEntityDetails(res.data);
          if (res?.data?.verificationStatus === 'True') {
            toast({
              title: "",
              description: "Verification Successful !!",
              variant: "success",
              duration: 10000
            });
            setIsVerified(true);
            setVerificationStatus(true);
          } else if (res?.data?.verificationStatus === 'False') {
            toast({
              title: "",
              description: "Verification Failed, Please try again !!",
              variant: "error",
              duration: 10000
            });
            setIsVerified(true);
            setVerificationStatus(false);
          } else {
            toast({
              title: "",
              description: "Verification Failed, Please try again !!",
              variant: "error",
              duration: 10000
            });
            setVerificationStatus(false);
            setIsVerified(false);
          }
        } else {
          toast({
            title: "",
            description: "Verification Failed, Please try again !!",
            variant: "error",
            duration: 10000,
          });
          setEntityDetails(null);
          setIsVerified(false);
          setVerificationStatus(false);
        }
      } else {
        toast({
          title: "",
          description: res?.message || res?.data?.message || "Verification Failed, Please try again !!",
          variant: "error",
          duration: 10000
        });
        setIsVerified(false);
        setVerificationStatus(false);
      }
    }).catch(err => {
      setEntityDetails(null);
      setIsVerified(false);
      setVerificationStatus(false);
      toast({
        title: "",
        description: data.message || "Verification Failed, Please try again !!",
        variant: "error",
        duration: 10000
      });

    }).finally(() => { setIsVerifying(false); })
  };

  const handleSubmit1 = () => {
    if (!declaration) {
      alert("Please accept the declaration to continue.");
      return;
    }
    dismiss();
    navigate("/peppol-registration", {
      state: { ...entityDetails, countryCode: watch('countryCode') || 'AE' }
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <PortalHeader text={'Linking Request'} description={''} />
      <main className="container mx-auto px-6 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="bg-card rounded-xl shadow-lg border border-border p-8 space-y-8">
            <h2 className="text-2xl font-bold text-foreground text-center border-b border-border pb-4">
              Enter Applicant Details
            </h2>

            <Form {...form}>
              <div className="space-y-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Registered EmaraTax Email Address <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          autoComplete="new-password"
                          type="email"
                          placeholder="example@company.com"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="TIN"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        TIN (Tax Identifier Number) <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          autoComplete="new-password"
                          placeholder=""
                          // maxLength={10}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="countryCode"
                  render={({ field }) => (
                    <FormItem>
                      <ReusableDropdown {...field} label="Select Country" options={countryOptions} onChange={field.onChange} value={field.value} />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="mobile"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Registered Mobile Number</FormLabel>
                      <FormControl>
                        <Input
                          type="tel"
                          placeholder="12345 67890"
                          // maxLength={10}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex justify-center pt-4">
                  <Button
                    type="button"
                    onClick={(data) => handleSubmit(handleVerify)(data)}
                    disabled={isVerifying || isVerified || isVerifyDisabled}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-base font-semibold rounded-lg shadow-md transition-all hover:shadow-lg disabled:opacity-50"
                  >
                    {isVerifying ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Verifying...
                      </>
                    ) : isVerified && verificationStatus ? (
                      "Verified ✓"
                    ) : (
                      "Verify Applicant Details"
                    )}
                  </Button>
                </div>
              </div>
            </Form>

            {isVerified && (
              <div className="space-y-6 pt-6 border-t border-border">
                <EntityDetailsSection details={entityDetails} />
                {isVerified && verificationStatus &&
                  <>
                    <div className="flex items-start gap-3 pt-4">
                      <Checkbox
                        id="declaration"
                        checked={declaration}
                        onCheckedChange={(checked) => setDeclaration(checked as boolean)}
                        className="mt-1"
                      />
                      <Label
                        htmlFor="declaration"
                        className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
                      >
                        I declare that all information provided is true, accurate and complete to the best of my knowledge and belief.
                      </Label>
                    </div>

                    <div className="flex justify-center pt-4">
                      <Button
                        onClick={handleSubmit1}
                        disabled={!declaration}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-base font-semibold rounded-lg shadow-md transition-all hover:shadow-lg disabled:opacity-50"
                      >
                        Submit
                      </Button>
                    </div>
                  </>
                }
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default LinkingRequest;
